/**
 * 進化一覧（図鑑）。
 * まだ出会っていない料理は「？」、出会ったがまだ作れていない料理は薄く、
 * 作れた料理は明るく表示する。
 * 「出会った」= NEXT に並んだか、合体で作った。Lv7以上は作るまで何が出るか分からない。
 * 解放状態は localStorage に保存され、次回起動時も引き継がれる。
 */

import type { GameContext } from '../game/GameContext';
import { getRewardStatus } from '../game/config/rewardStatus';
import type { StageItemDefinition, TourHotspot } from '../game/config/types';

/** ごほうびモーダルを開くイベント。 */
type RewardOpenHandler = (item: StageItemDefinition) => void;
/** 豆知識カードを開くイベント。 */
type TipOpenHandler = (hotspot: TourHotspot) => void;

export class EvolutionList {
  private readonly context: GameContext;
  private readonly listEl: HTMLElement | null;
  private readonly panelEl: HTMLElement | null;
  private readonly progressEl: HTMLElement | null;
  private readonly tourListEl: HTMLElement | null;
  private readonly tourPanelEl: HTMLElement | null;
  private readonly tourProgressEl: HTMLElement | null;
  private readonly nodes = new Map<string, HTMLLIElement>();
  private readonly tourNodes = new Map<string, HTMLLIElement>();
  private unlocked: Set<string>;
  private seen: Set<string>;
  private discoveredTips: Set<string>;
  private onRewardOpen?: RewardOpenHandler;
  private onTipOpen?: TipOpenHandler;

  constructor(context: GameContext, root: ParentNode = document) {
    this.context = context;
    this.listEl = root.querySelector<HTMLElement>('[data-ui="evolution-list"]');
    this.panelEl = root.querySelector<HTMLElement>('[data-ui="evolution-panel"]');
    this.progressEl = root.querySelector<HTMLElement>('[data-ui="dex-progress"]');
    this.tourListEl = root.querySelector<HTMLElement>('[data-ui="tour-list"]');
    this.tourPanelEl = root.querySelector<HTMLElement>('[data-ui="tour-panel"]');
    this.tourProgressEl = root.querySelector<HTMLElement>('[data-ui="tour-progress"]');
    this.unlocked = new Set(context.storage.unlockedItemIds);
    this.seen = new Set(context.storage.seenItemIds);
    this.discoveredTips = new Set(context.storage.discoveredTipIds);

    this.render();
    this.renderTour();
    this.applyVisibility();

    context.bus.on('dex:unlock', ({ item }) => this.unlock(item, true));
    // NEXT に並んだ料理は「出会った」ことにして、名前と絵を見せる
    context.bus.on('queue:change', ({ current, next }) => {
      this.reveal(current);
      this.reveal(next);
    });
    context.bus.on('reward:unlock', ({ item }) => this.updateReward(item.id));
    context.bus.on('total:change', () => this.refreshRewards());
    context.bus.on('settings:change', () => this.applyVisibility());
    context.bus.on('tour:discover', ({ tipId }) => this.discoverTip(tipId));
  }

  setRewardOpenHandler(handler: RewardOpenHandler): void {
    this.onRewardOpen = handler;
  }

  setTipOpenHandler(handler: TipOpenHandler): void {
    this.onTipOpen = handler;
  }

  private render(): void {
    if (!this.listEl) return;
    this.listEl.replaceChildren();
    this.nodes.clear();

    this.context.items.forEach((item) => {
      const node = document.createElement('li');
      node.className = 'evolution-item';
      node.dataset.unlocked = this.unlocked.has(item.id) ? 'true' : 'false';
      node.dataset.revealed = this.isRevealed(item) ? 'true' : 'false';
      node.setAttribute('role', 'listitem');
      if (item.reward) {
        node.style.cursor = 'pointer';
      }

      const icon = document.createElement('img');
      icon.className = 'evolution-item__icon';
      icon.width = 42;
      icon.height = 42;
      icon.alt = '';
      icon.setAttribute('aria-hidden', 'true');

      const mystery = document.createElement('span');
      mystery.className = 'evolution-item__mystery';
      mystery.textContent = '？';
      mystery.setAttribute('aria-hidden', 'true');

      const level = document.createElement('span');
      level.className = 'evolution-item__level';
      level.textContent = `Lv.${item.level}`;

      const name = document.createElement('span');
      name.className = 'evolution-item__name';

      const reward = document.createElement('span');
      reward.className = 'evolution-item__reward';
      reward.textContent = '食べに行く';
      reward.setAttribute('aria-hidden', 'true');
      reward.style.display = 'none';

      node.append(icon, mystery, level, name, reward);
      this.applyReveal(node, item);
      this.updateRewardNode(node, item);

      node.addEventListener('click', () => this.onRewardOpen?.(item));

      this.nodes.set(item.id, node);
      this.listEl!.append(node);
    });

    this.updateProgress();
  }

  private updateRewardNode(node: HTMLElement, item: StageItemDefinition): void {
    const status = getRewardStatus(
      item,
      [...this.unlocked, ...this.seen],
      this.context.storage.totalScore,
    );

    const available = status.available;
    node.dataset.reward = available ? 'available' : status.discovered ? 'discovered' : 'locked';
    if (!this.isRevealed(item)) {
      node.setAttribute('aria-label', `レベル${item.level} まだ出会っていない料理`);
      node.title = 'まだ出会っていない料理';
    } else {
      node.setAttribute(
        'aria-label',
        `レベル${item.level} ${item.name} ${this.unlocked.has(item.id) ? '作成済み' : 'まだ作っていません'}${
          available ? '。食べに行けます' : ''
        }`,
      );
      node.title = item.description ? `${item.name}：${item.description}` : item.name;
    }

    const rewardBadge = node.querySelector<HTMLElement>('.evolution-item__reward');
    if (rewardBadge) {
      rewardBadge.style.display = item.reward ? '' : 'none';
      rewardBadge.textContent = available ? '食べに行く' : '出会うと解放';
    }
  }

  private updateReward(itemId: string): void {
    const item = this.context.itemByLevel.get(
      this.context.items.findIndex((i) => i.id === itemId) + 1,
    );
    if (!item) return;
    this.updateRewardNodeIfExists(item);
  }

  private refreshRewards(): void {
    this.context.items.forEach((item) => this.updateRewardNodeIfExists(item));
  }

  private updateRewardNodeIfExists(item: StageItemDefinition): void {
    const node = this.nodes.get(item.id);
    if (!node) return;
    this.updateRewardNode(node, item);
  }

  private isRevealed(item: StageItemDefinition): boolean {
    return this.unlocked.has(item.id) || this.seen.has(item.id);
  }

  /** 名前と絵を出すか「？」にするかを当てる。 */
  private applyReveal(node: HTMLElement, item: StageItemDefinition): void {
    const revealed = this.isRevealed(item);
    node.dataset.revealed = String(revealed);
    const icon = node.querySelector<HTMLImageElement>('.evolution-item__icon');
    const name = node.querySelector<HTMLElement>('.evolution-item__name');
    // まだ出会っていない料理は画像の URL も入れない（開発者ツールから覗けないように）
    if (icon) {
      if (revealed) {
        if (!icon.getAttribute('src')) icon.src = this.context.icons.iconUrl(item, 96);
      } else {
        icon.removeAttribute('src');
      }
    }
    if (name) name.textContent = revealed ? (item.shortName ?? item.name) : '？？？';
  }

  /** NEXT に並んだ料理を「出会った」にする。 */
  private reveal(item: StageItemDefinition): void {
    if (this.seen.has(item.id)) return;
    this.seen.add(item.id);
    this.context.storage.markSeen(item.id);
    const node = this.nodes.get(item.id);
    if (!node) return;
    this.applyReveal(node, item);
    this.updateRewardNode(node, item);
    node.dataset.justUnlocked = 'true';
    window.setTimeout(() => delete node.dataset.justUnlocked, 500);
  }

  private unlock(item: StageItemDefinition, animate: boolean): void {
    if (this.unlocked.has(item.id)) return;
    this.unlocked.add(item.id);
    this.seen.add(item.id);
    const node = this.nodes.get(item.id);
    if (node) {
      node.dataset.unlocked = 'true';
      this.applyReveal(node, item);
      this.updateRewardNode(node, item);
      if (animate) {
        node.dataset.justUnlocked = 'true';
        window.setTimeout(() => delete node.dataset.justUnlocked, 500);
      }
    }
    this.updateProgress();
  }

  private updateProgress(): void {
    if (!this.progressEl) return;
    this.progressEl.textContent = `${this.unlocked.size} / ${this.context.items.length}`;
  }

  /* ----------------------------- 福岡めぐり欄 ----------------------------- */

  private renderTour(): void {
    if (!this.tourListEl) return;
    this.tourListEl.replaceChildren();
    this.tourNodes.clear();

    const tour = this.context.stage.scenery?.tour;
    if (!tour) {
      if (this.tourPanelEl) this.tourPanelEl.style.display = 'none';
      return;
    }

    if (this.tourPanelEl) this.tourPanelEl.style.display = '';

    const allHotspots = tour.scenes.flatMap((scene) => scene.hotspots);
    allHotspots.forEach((hotspot) => {
      const node = document.createElement('li');
      node.className = 'tour-item';
      node.dataset.discovered = this.discoveredTips.has(hotspot.id) ? 'true' : 'false';
      node.setAttribute('role', 'listitem');

      const discovered = this.discoveredTips.has(hotspot.id);
      this.applyTourItemInteractivity(node, discovered);

      const title = document.createElement('span');
      title.className = 'tour-item__title';
      title.textContent = discovered ? hotspot.title : '？？？';

      const area = document.createElement('span');
      area.className = 'tour-item__area';
      area.textContent = discovered
        ? this.findSceneName(hotspot.id) ?? ''
        : '背景で見つけよう';

      node.append(title, area);
      // 未発見の豆知識は図鑑から開けない。発見の経路は背景のマーカーだけに限る。
      // discoveredTips を毎回参照するので、あとから発見しても再描画なしで押せるようになる。
      node.addEventListener('click', () => {
        if (!this.discoveredTips.has(hotspot.id)) return;
        this.onTipOpen?.(hotspot);
      });

      this.tourNodes.set(hotspot.id, node);
      this.tourListEl!.append(node);
    });

    this.updateTourProgress(allHotspots.length);
  }

  /** 発見状態に応じて、図鑑のツアー項目を押せる／押せない見た目にする。 */
  private applyTourItemInteractivity(node: HTMLElement, discovered: boolean): void {
    node.style.cursor = discovered ? 'pointer' : 'default';
    if (discovered) {
      node.removeAttribute('aria-disabled');
    } else {
      node.setAttribute('aria-disabled', 'true');
    }
  }

  private findSceneName(hotspotId: string): string | null {
    const tour = this.context.stage.scenery?.tour;
    if (!tour) return null;
    const scene = tour.scenes.find((scene) =>
      scene.hotspots.some((h) => h.id === hotspotId),
    );
    return scene ? `${scene.name} / ${scene.area}` : null;
  }

  private discoverTip(tipId: string, animate = false): void {
    if (this.discoveredTips.has(tipId)) return;
    this.discoveredTips.add(tipId);

    const node = this.tourNodes.get(tipId);
    const hotspot = this.findHotspot(tipId);
    if (node && hotspot) {
      node.dataset.discovered = 'true';
      const title = node.querySelector<HTMLElement>('.tour-item__title');
      const area = node.querySelector<HTMLElement>('.tour-item__area');
      if (title) title.textContent = hotspot.title;
      if (area) area.textContent = this.findSceneName(tipId) ?? '';
      this.applyTourItemInteractivity(node, true);
      if (animate) {
        node.dataset.justDiscovered = 'true';
        window.setTimeout(() => delete node.dataset.justDiscovered, 500);
      }
    }

    const total = this.context.stage.scenery?.tour?.scenes.reduce(
      (sum, scene) => sum + scene.hotspots.length,
      0,
    );
    this.updateTourProgress(total ?? 0);
  }

  private findHotspot(id: string): TourHotspot | null {
    return (
      this.context.stage.scenery?.tour?.scenes
        .flatMap((scene) => scene.hotspots)
        .find((h) => h.id === id) ?? null
    );
  }

  private updateTourProgress(total: number): void {
    if (!this.tourProgressEl) return;
    this.tourProgressEl.textContent = `${this.discoveredTips.size} / ${total}`;
  }

  private applyVisibility(): void {
    if (!this.panelEl) return;
    this.panelEl.dataset.visible = this.context.settings.showEvolutionList ? 'true' : 'false';
  }

  /** 図鑑表示を保存データから再読み込みする。 */
  refresh(): void {
    this.unlocked = new Set(this.context.storage.unlockedItemIds);
    this.discoveredTips = new Set(this.context.storage.discoveredTipIds);
    this.render();
    this.renderTour();
  }
}
